import { useState } from "react";
import { useAuth } from "@/App";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { StockArticle } from "@/lib/stock.api";
import { RestockDialog } from "./stock/RestockDialog";
import { ArticleDetailDialog } from "./stock-articles/components/ArticleDetailDialog";
import { EditArticleDialog } from "./stock-articles/components/EditArticleDialog";
import { MovementDetailDialog } from "./stock-articles/components/MovementDetailDialog";
import { StockArticlesTabContent } from "./stock-articles/components/StockArticlesTabContent";
import { StockKpiGrid } from "./stock-articles/components/StockKpiGrid";
import { StockMovementsTabContent } from "./stock-articles/components/StockMovementsTabContent";
import { StockPageIntro } from "./stock-articles/components/StockPageIntro";
import { StockTabsToolbar } from "./stock-articles/components/StockTabsToolbar";
import { STOCK_PAGE_GUIDE, STOCK_PAGE_TIP, STOCK_TABS } from "./stock-articles/stock.constants";
import { useStockArticleActions } from "./stock-articles/hooks/useStockArticleActions";
import { useStockArticlesPageData } from "./stock-articles/hooks/useStockArticlesPageData";
import { useStockArticlesPageState } from "./stock-articles/hooks/useStockArticlesPageState";
import { useAttributionReassignmentFlag } from "@/hooks/queries/useAttributionReassignmentFlag";
import { canUseReassignment } from "@/lib/reassignment";

export default function StockArticlesPage() {
  const { user } = useAuth();
  const pageState = useStockArticlesPageState();
  const pageData = useStockArticlesPageData(pageState.includeInactive);
  const actions = useStockArticleActions();
  const canManage = Boolean(user && ["manager", "admin", "super_admin"].includes(user.role));
  // admin+ edit article fields and apply ADJUST directly; a manager must submit
  // a modification request for both (see stock-edit.service.ts server-side).
  const canEditDirect = Boolean(user && ["admin", "super_admin"].includes(user.role));
  const canDelete = user?.role === "super_admin";
  const { data: reassignmentFlag } = useAttributionReassignmentFlag();
  const canReassign = canUseReassignment(user?.role, reassignmentFlag);
  const [pendingDelete, setPendingDelete] = useState<StockArticle | null>(null);
  const [editingArticle, setEditingArticle] = useState<StockArticle | null>(null);
  const [restockingArticle, setRestockingArticle] = useState<StockArticle | null>(null);

  usePageHeader({
    title: "Stock",
    helpTopic: "stock",
    guide: {
      steps: STOCK_PAGE_GUIDE,
      tip: STOCK_PAGE_TIP,
    },
  });

  return (
    <div>
      <StockPageIntro canManage={canManage} onCreated={actions.reloadStock} />
      <StockKpiGrid
        articlesCount={pageData.articles.length}
        loadingMovements={pageData.loadingMovements}
        metrics={pageData.metrics}
      />

      <Tabs defaultValue={STOCK_TABS.articles}>
        <StockTabsToolbar
          includeInactive={pageState.includeInactive}
          onIncludeInactiveChange={pageState.setIncludeInactive}
        />

        <TabsContent value={STOCK_TABS.articles}>
          <StockArticlesTabContent
            articles={pageData.articles}
            isMobile={pageState.isMobile}
            isLoading={pageData.loadingArticles}
            canManage={canManage}
            toggling={actions.toggling}
            onSelect={pageState.setSelectedArticle}
            onToggleActive={actions.toggleActive}
            onEdit={setEditingArticle}
            onRestock={setRestockingArticle}
          />
        </TabsContent>

        <TabsContent value={STOCK_TABS.movements}>
          <StockMovementsTabContent
            movements={pageData.movements}
            isMobile={pageState.isMobile}
            isLoading={pageData.loadingMovements}
            onSelect={pageState.setSelectedMovement}
          />
        </TabsContent>
      </Tabs>

      <ArticleDetailDialog
        article={pageState.selectedArticle}
        canManage={canManage}
        toggling={actions.toggling}
        onOpenChange={(open) => {
          if (!open) pageState.setSelectedArticle(null);
        }}
        onToggleActive={actions.toggleActive}
        canDelete={canDelete}
        deleting={actions.deleting}
        onDelete={(article) => setPendingDelete(article)}
        onEdit={setEditingArticle}
        onRestock={(article) => {
          pageState.setSelectedArticle(null);
          setRestockingArticle(article);
        }}
      />

      <EditArticleDialog
        article={editingArticle}
        canEditDirect={canEditDirect}
        onClose={() => setEditingArticle(null)}
        onUpdated={actions.reloadStock}
      />

      <RestockDialog
        article={restockingArticle}
        canAdjustDirect={canEditDirect}
        onClose={() => setRestockingArticle(null)}
        onDone={actions.reloadStock}
      />

      <MovementDetailDialog
        movement={pageState.selectedMovement}
        canReassign={canReassign}
        onOpenChange={(open) => {
          if (!open) pageState.setSelectedMovement(null);
        }}
        onReassigned={actions.reloadStock}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Supprimer cet article ?"
        description="Bloqué si l'article a un historique de mouvements de stock. Reste restaurable depuis la Corbeille."
        confirmLabel="Supprimer"
        variant="destructive"
        loading={actions.deleting}
        onConfirm={() => {
          if (!pendingDelete) return;
          actions.deleteArticle(pendingDelete, () => {
            pageState.setSelectedArticle(null);
            setPendingDelete(null);
          });
        }}
      />
    </div>
  );
}
